import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import {getButtonsData} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import type {Context} from 'src/common/types'

export type ComparisonTableProps = SliceComponentProps<Content.ComparisonTableSlice>

const paddingTopMap: Record<string, string> = {
  None: '',
  'X-Small (D:16px / M:8px)': 'pt-2 md:pt-4',
  'Small (D:24px / M:16px)': 'pt-4 md:pt-6',
  'Medium (D:48px / M:32px)': 'pt-8 md:pt-12',
  'Large (D:96px / M:48px)': 'pt-12 md:pt-24',
}

const paddingBottomMap: Record<string, string> = {
  None: '',
  'X-Small (D:16px / M:8px)': 'pb-2 md:pb-4',
  'Small (D:24px / M:16px)': 'pb-4 md:pb-6',
  'Medium (D:48px / M:32px)': 'pb-8 md:pb-12',
  'Large (D:96px / M:48px)': 'pb-12 md:pb-24',
}

const TABLE_COLS = 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr)'

const CheckMoss = () => (
  <span
    aria-hidden="true"
    className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-green-neon text-[11px] font-bold text-moss-black"
  >
    ✓
  </span>
)

const CheckCompetitor = () => (
  <span
    aria-hidden="true"
    className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-moss-grey-200 text-[11px] font-bold text-moss-black"
  >
    ✓
  </span>
)

const Cross = () => (
  <span
    aria-hidden="true"
    className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-black/[0.08] text-[11px] text-black/55"
  >
    —
  </span>
)

const MossCell = ({
  label,
  detail,
  iconType = 'check',
}: {
  label: string
  detail?: string | null
  iconType?: 'check' | 'cross'
}) => (
  <div className="border-l border-black/[0.06] bg-green-neon/[0.07] px-2 py-[22px] md:px-7">
    <div className="flex flex-col items-center gap-1.5 md:flex-row md:items-center md:gap-2">
      {iconType === 'cross' ? <Cross /> : <CheckMoss />}
      <span
        className={`text-center text-[13px] font-medium md:text-left md:text-[15px] ${iconType === 'cross' ? 'text-black/55' : 'text-moss-black'}`}
      >
        {label}
      </span>
    </div>
    {detail && <div className="mt-1 text-center text-[12px] text-black/60 md:text-left">{detail}</div>}
  </div>
)

const CompetitorCell = ({
  label,
  detail,
  iconType = 'check',
}: {
  label: string
  detail?: string | null
  iconType?: 'check' | 'cross'
}) => (
  <div className="border-l border-black/[0.06] px-2 py-[22px] md:px-7">
    <div className="flex flex-col items-center gap-1.5 md:flex-row md:items-center md:gap-2">
      {iconType === 'cross' ? <Cross /> : <CheckCompetitor />}
      <span
        className={`text-center text-[13px] font-medium md:text-left md:text-[15px] ${iconType === 'cross' ? 'text-black/55' : 'text-moss-black'}`}
      >
        {label}
      </span>
    </div>
    {detail && <div className="mt-1 text-center text-[12px] text-black/60 md:text-left">{detail}</div>}
  </div>
)

const FeatureCell = ({label, src}: {label: string; src: string}) => (
  <div className="flex flex-col gap-1 px-2 py-[22px] text-[15px] font-medium text-moss-black md:px-7">
    {label}
    <span className="text-[12px] font-normal text-black/45">{src}</span>
  </div>
)

const GroupRow = ({dotClass, label}: {dotClass: string; label: string}) => (
  <div className="flex items-center justify-center gap-[10px] border-t border-black/[0.08] bg-grey-120 px-7 py-[18px] text-[11px] font-semibold uppercase tracking-widest text-moss-black md:justify-start">
    <span className={cn('size-2 shrink-0 rounded-full', dotClass)} />
    {label}
  </div>
)

const TableRowGroup = ({
  label,
  dotClass,
  rows,
}: {
  label?: string | null
  dotClass: string
  rows: ReadonlyArray<{
    title?: string | null
    subtitle?: string | null
    moss_icon_type?: string | null
    moss_evaluation?: string | null
    moss_detail?: string | null
    competitor_icon_type?: string | null
    competitor_evaluation?: string | null
    competitor_detail?: string | null
  }>
}) => {
  if (rows.length === 0) {
    return null
  }

  return (
    <>
      {label && <GroupRow dotClass={dotClass} label={label} />}
      {rows.map((item, i) => (
        <div key={i} className="grid border-t border-black/[0.06]" style={{gridTemplateColumns: TABLE_COLS}}>
          <FeatureCell label={item.title || ''} src={item.subtitle || ''} />
          <MossCell
            label={item.moss_evaluation || ''}
            detail={item.moss_detail}
            iconType={(item.moss_icon_type as 'check' | 'cross') ?? 'check'}
          />
          <CompetitorCell
            label={item.competitor_evaluation || ''}
            detail={item.competitor_detail}
            iconType={(item.competitor_icon_type as 'check' | 'cross') ?? 'check'}
          />
        </div>
      ))}
    </>
  )
}

const ComparisonTable = ({
  slice,
  context: {pageUid, buttonsData},
}: ComparisonTableProps & {context: Context}): JSX.Element => {
  const {linkResolver} = useLinkResolver()
  const domainCountry = useGetDomainCountry()

  const {primaryButtonData: buttonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.book_an_intro_button,
  })

  const buttonHref = buttonData?.link ? linkResolver(buttonData.link, 'comparison-cta') : null
  const buttonLabel = buttonData?.label

  const trackButtonClick = (location: string) => {
    trackMossAnalytics({
      event: 'interaction',
      interaction: {
        element: buttonData?.marketing_id || location,
        elementText: buttonLabel || '',
        location,
        type: 'click',
      },
      page: {domainCountry, type: pageUid || 'unknown'},
    })
  }

  const p = slice.primary
  const isDark = p.background_color === 'Dark green'

  const bgMap: Record<string, string> = {
    'Dark green': 'bg-green-dark',
    'Grey/100': 'bg-moss-grey-100',
    'Moss White': 'bg-moss-white',
  }

  const bgClass = bgMap[p.background_color ?? 'Grey/100'] ?? 'bg-moss-grey-100'
  const ptClass = paddingTopMap[p.padding_top ?? 'Large (D:96px / M:48px)'] ?? ''
  const pbClass = paddingBottomMap[p.padding_bottom ?? 'Large (D:96px / M:48px)'] ?? ''
  const competitorName = p.competitor_name || 'Competitor'

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      data-nav-dark={isDark || undefined}
      className={cn(bgClass, ptClass, pbClass, 'text-moss-black')}
      id="compare"
    >
      <div className="mx-auto max-w-[1280px] px-5 md:px-10">
        <div className="mb-12 max-w-[720px]">
          {p.eyebrow && (
            <p
              className={cn(
                'text-[11px] font-semibold uppercase tracking-widest',
                isDark ? 'text-green-neon' : 'text-green-deep',
              )}
            >
              {p.eyebrow}
            </p>
          )}
          {p.heading && <h2 className="text-h3 md:text-h2 mb-5 mt-4 text-balance text-moss-black">{p.heading}</h2>}
          {p.subtext && <p className="text-[19px] leading-relaxed text-black/65">{p.subtext}</p>}
        </div>

        <div className="overflow-visible rounded-xl border border-black/[0.08] bg-moss-white">
          <div
            className="sticky top-[68px] z-10 grid rounded-t-xl bg-moss-black text-moss-white shadow-md"
            style={{gridTemplateColumns: TABLE_COLS}}
          >
            <div className="flex items-center px-2 py-[22px] text-[11px] font-semibold uppercase tracking-widest text-moss-white/55 md:px-7">
              {p.feature_column_label || 'Feature'}
            </div>
            <div className="flex flex-col items-start gap-3 border-l border-moss-white/10 px-2 py-[22px] md:flex-row md:items-center md:px-7">
              <div className="flex items-center gap-1 text-[18px] font-medium text-moss-white md:gap-[10px]">
                <Image src="/icons/moss-icon-white.svg" alt="" height={18} width={18} />
                <span>Moss</span>
              </div>
              {buttonHref && buttonLabel && (
                <Link
                  href={buttonHref}
                  className="text-button-s hidden h-12 items-center whitespace-nowrap rounded-[4px] bg-green-neon px-4 py-3 uppercase text-black hover:bg-neon-550 md:inline-flex"
                  onClick={() => trackButtonClick('comparison-table-header')}
                >
                  {buttonLabel}
                </Link>
              )}
            </div>
            <div className="flex items-center border-l border-moss-white/10 px-2 py-[22px] text-[18px] font-medium text-moss-white md:px-7">
              {competitorName}
            </div>
          </div>

          <TableRowGroup
            label={p.table_rows_moss_advantage_label}
            dotClass="bg-green-neon"
            rows={p.table_rows_moss_advantage}
          />
          <TableRowGroup label={p.table_rows_shared_label} dotClass="bg-grey-200" rows={p.table_rows_shared} />
          <TableRowGroup
            label={p.table_rows_competitor_advantage_label}
            dotClass="bg-orange-300"
            rows={p.table_rows_competitor_advantage}
          />
        </div>

        {isFilled.richText(p.footnote) && (
          <div className="mt-6 max-w-[880px] text-[12px] leading-relaxed text-black/45">
            <PrismicRichText field={p.footnote} />
          </div>
        )}
      </div>
    </section>
  )
}

export default ComparisonTable
